import Api from '@/api'
import { normalizeCompanyTradieCsvImportResponse } from '@/utils/tradieCsvImportErrors'

export default {
  namespaced: true,

  state: {
    company: null,

    tradies: [],
    tradiesMeta: null,
    tradie: null,

    files: [],
    requestedFiles: [],

    resumes: [],
    resumesMeta: null,

    companyServices: [],

    canSendMassNotification: null,
  },

  getters: {
    company(state) {
      return state.company
    },

    // Tradies
    tradies(state) {
      return state.tradies
    },
    tradiesMeta(state) {
      return state.tradiesMeta
    },
    tradie(state) {
      return state.tradie
    },

    // Tradie's Files
    files(state) {
      return state.files
    },
    requestedFiles(state) {
      return state.requestedFiles
    },

    // Resumes
    resumes(state) {
      return state.resumes
    },
    resumesMeta(state) {
      return state.resumesMeta
    },

    // Company Services
    companyServices(state) {
      return state.companyServices
    },

    canSendMassNotification(state) {
      return state.canSendMassNotification
    },
  },

  mutations: {
    setCompany(state, company) {
      state.company = company
    },
    clearCompany(state) {
      state.company = null
    },

    setTradies(state, tradies) {
      state.tradies = tradies
    },
    setTradiesMeta(state, tradiesMeta) {
      state.tradiesMeta = tradiesMeta
    },
    clearTradies(state) {
      state.tradies = []
    },

    setTradie(state, tradie) {
      state.tradie = tradie
    },
    clearTradie(state) {
      state.tradie = null
    },

    setFiles(state, files) {
      state.files = files
    },
    clearFiles(state) {
      state.files = []
    },
    updateFileCompanyApproval(state, payload) {
      if (!payload || !Array.isArray(state.files)) return
      const idx = state.files.findIndex((f) => f && f.id === payload.id)
      if (idx === -1) return
      const merged = {
        ...state.files[idx],
        company_approval: payload.company_approval,
        company_approval_reason: payload.company_approval_reason,
        company_approval_at: payload.company_approval_at,
        company_approval_by: payload.company_approval_by,
      }
      state.files.splice(idx, 1, merged)
    },

    setRequestedFiles(state, requestedFiles) {
      state.requestedFiles = requestedFiles
    },
    clearRequestedFiles(state) {
      state.requestedFiles = []
    },

    setResumes(state, resumes) {
      state.resumes = resumes
    },
    setResumesMeta(state, resumesMeta) {
      state.resumesMeta = resumesMeta
    },
    clearResumes(state) {
      state.resumes = []
    },

    setCompanyServices(state, companyServices) {
      state.companyServices = companyServices
    },

    setCanSendMassNotification(state, value) {
      state.canSendMassNotification = value
    },
  },

  actions: {
    async getCompany({ commit }, id) {
      await new Promise(async (resolve, reject) => {
        Api.get(`companies/${id}`)
          .then((res) => {
            commit('setCompany', res.data.data)
            resolve(res.data.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async updateCompany({ commit }, params) {
      const { data } = await Api.put(`companies/${params.id}`, params.formData)
      commit('setCompany', data.data)
    },

    async resendInvitation({ commit }, tradieId) {
      await new Promise(async (resolve, reject) => {
        Api.post('companies/resend-invitation', { tradie_id: tradieId })
          .then((res) => resolve(res.data.data))
          .catch((err) => reject(err.response.data || err.response))
      })
    },

    async getTradies({ commit }, params) {
      commit('clearTradies')

      await new Promise(async (resolve, reject) => {
        Api.get(
          `companies/tradies?limit=${params.limit}&page=${params.page}&search=${params.search}` +
            `&archived_only=${params.archivedOnly ? 'true' : 'false'}` +
            (params.supervisorOnly ? '&supervisor_only=true' : '') +
            (params.sortBy
              ? `&sort_by=${params.sortBy}&sort_dir=${params.sortDir}`
              : ''),
        )
          .then((res) => {
            const tradies = Array.isArray(res.data.data) ? res.data.data : []

            commit('setTradies', tradies)
            commit('setTradiesMeta', res.data.meta || null)
            resolve(tradies)
          })
          .catch((err) => reject(err.response))
      })
    },
    async updateTradieSupervisor(
      { commit },
      { tradieId, isSupervisor, projectIds },
    ) {
      // eslint-disable-next-line camelcase
      const res = await Api.put(`companies/tradie/${tradieId}/supervisor`, {
        is_supervisor: isSupervisor, // eslint-disable-line camelcase
        project_ids: projectIds || [], // eslint-disable-line camelcase
      })
      return res.data
    },

    async archiveTradie({ commit }, { tradieId, isArchived }) {
      await new Promise(async (resolve, reject) => {
        await Api.post(`companies/tradie/${tradieId}/archive`, {
          is_archived: isArchived,
        })
          .then((res) => resolve(res.data.data))
          .catch((err) => resolve(err.response.data || err.response))
      })
    },

    // Accepts either a bare email string (company-wide invite, no project
    // context) or { email, projectId } when the caller already knows which
    // project to attach the tradie to (e.g. inviting from that project's
    // timesheet screen) — projectId is omitted from the request when absent.
    async addTradie({ commit }, arg) {
      const email = typeof arg === 'string' ? arg : arg.email
      const projectId = typeof arg === 'string' ? null : arg.projectId
      return new Promise((resolve, reject) => {
        Api.post('companies/add-tradie', {
          email,
          ...(projectId ? { project_id: projectId } : {}),
        })
          .then((res) => {
            const d = res.data
            const payload =
              d && d.data != null && typeof d.data === 'object'
                ? d.data
                : d && typeof d.message === 'string'
                ? { message: d.message }
                : d || {}
            resolve(payload)
          })
          .catch((err) => reject(err.response.data))
      })
    },

    async importTradieCSV({ commit }, data) {
      const formData = new FormData()
      formData.append('csv_file', data.tradieCsvFile)

      return new Promise((resolve, reject) => {
        Api.post(`companies/import-tradie-csv`, formData)
          .then((res) => resolve(normalizeCompanyTradieCsvImportResponse(res)))
          .catch((err) => reject(err.response))
      })
    },

    async removeTradie({ commit }, tradieId) {
      await new Promise(async (resolve, reject) => {
        Api.post('companies/remove-tradie', { tradie_id: tradieId })
          .then((res) => resolve(res.data.data))
          .catch((err) => reject(err.response.data))
      })
    },

    async getTradie({ commit }, data) {
      commit('clearTradie')
      commit('clearFiles')
      commit('clearRequestedFiles')

      await new Promise(async (resolve, reject) => {
        Api.get(`companies/tradie/${data.tradieId}`)
          .then((res) => {
            commit('setTradie', res.data.data)
            commit('setFiles', res.data.files)
            commit('setRequestedFiles', res.data.requested_files)
            resolve(res.data.data)
          })
          .catch((err) => reject(err.response))
      })
    },
    async markNa({ commit }, { tradieId, fileId, projectId }) {
      if (!tradieId || !fileId) {
        return Promise.reject(new Error('Missing required IDs'))
      }
      await new Promise(async (resolve, reject) => {
        Api.post('/mark-na', {
          tradie_id: tradieId,
          file_id: fileId,
          project_id: projectId,
        })
          .then((res) => resolve(res.data.data))
          .catch((err) => reject(err.response.data))
      })
    },
    async getResumes({ commit }, params) {
      await new Promise(async (resolve, reject) => {
        // prettier-ignore
        Api.get(`companies/resumes?limit=${params.limit}&page=${params.page}&search=${params.search}`
          + (params.sortBy ? `&sort_by=${params.sortBy}&sort_dir=${params.sortDir}` : ''))
          .then((res) => {
            commit('setResumes', res.data.data)
            commit('setResumesMeta', res.data.meta)
            resolve(res.data.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async removeResume({ commit }, resumeId) {
      await new Promise(async (resolve, reject) => {
        Api.delete(`companies/resumes/${resumeId}`)
          .then((res) => resolve(res.data))
          .catch((err) => reject(err.response))
      })
    },

    async getCompanyServicePickerValues({ commit }) {
      await new Promise(async (resolve, reject) => {
        await Api.get('/company-services/picker-values')
          .then((res) => {
            commit('setCompanyServices', res.data.data)
            resolve(res.data.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async fetchCanSendMassNotification({ commit }) {
      try {
        const { data } = await Api.get('companies/can-send-mass-notification')
        commit('setCanSendMassNotification', data.can_send === true)
      } catch (e) {
        commit('setCanSendMassNotification', false)
      }
    },
  },
}
